import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { API_URL } from '../config/api';

const AdminQuickBooking = () => {
  const { branding } = useBranding();
  const primaryColor = branding?.primaryColor || '#667eea';
  const [sports, setSports] = useState([]);
  const [courts, setCourts] = useState([]);
  const [pricingRules, setPricingRules] = useState([]);
  const [selectedSport, setSelectedSport] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [availability, setAvailability] = useState({});
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showBookingForm, setShowBookingForm] = useState(false);

  const [customerData, setCustomerData] = useState({
    name: '',
    phone: '',
    email: '',
    billNumber: '',
    adminNotes: '',
    paymentMode: 'cash'
  });

  const [discount, setDiscount] = useState({
    percentage: 0,
    reason: ''
  });

  const { token, user } = useAuth();

  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 6; hour < 22; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const startHour = hour.toString().padStart(2, '0');
        const startMinute = minute.toString().padStart(2, '0');
        const endHour = minute === 30 ? (hour + 1).toString().padStart(2, '0') : hour.toString().padStart(2, '0');
        const endMinute = minute === 30 ? '00' : '30';

        const startTime = `${startHour}:${startMinute}:00`;
        const endTime = `${endHour}:${endMinute}:00`;
        const displayTime = `${startHour}:${startMinute} - ${endHour}:${endMinute}`;

        slots.push({ startTime, endTime, displayTime, id: `${startTime}-${endTime}` });
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const calculateSlotPrice = (courtId, time) => {
    const pricing = pricingRules.find(p => p.court_id === courtId);
    if (!pricing || !pricing.time_slots) return 150;

    const date = new Date(selectedDate);
    const dayOfWeek = date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const hour = parseInt(time.split(':')[0]);

    const slots = isWeekend ? pricing.time_slots.weekend : pricing.time_slots.weekday;
    let hourlyPrice = 0;

    if (isWeekend) {
      if (hour >= 6 && hour < 18) {
        hourlyPrice = slots['06:00-18:00'] || 300;
      } else if (hour >= 18 && hour < 22) {
        hourlyPrice = slots['18:00-22:00'] || 300;
      }
    } else {
      if (hour >= 6 && hour < 11) {
        hourlyPrice = slots['06:00-11:00'] || 300;
      } else if (hour >= 11 && hour < 16) {
        hourlyPrice = slots['11:00-16:00'] || 300;
      } else if (hour >= 16 && hour < 18) {
        hourlyPrice = slots['16:00-18:00'] || 300;
      } else if (hour >= 18 && hour < 22) {
        hourlyPrice = slots['18:00-22:00'] || 300;
      }
    }

    return hourlyPrice / 2;
  };

  const fetchPricingRules = async () => {
    try {
      const response = await fetch(`${API_URL}/pricing`);
      if (response.ok) {
        const data = await response.json();
        setPricingRules(data);
      }
    } catch (error) {
      console.error('Error fetching pricing rules:', error);
    }
  };

  const fetchCourts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/courts`);

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      let courtsData = [];

      if (data.success && data.courts) {
        courtsData = data.courts;
      } else if (data.data) {
        courtsData = data.data;
      } else if (Array.isArray(data)) {
        courtsData = data;
      }

      setCourts(courtsData);

      const uniqueSports = [...new Set(courtsData.map(c => c.sport_type || c.sport_name))].filter(Boolean);
      setSports(uniqueSports);

      setError('');
    } catch (error) {
      console.error('Error fetching courts:', error);
      setError('Failed to connect to server.');
    } finally {
      setLoading(false);
    }
  };

  const checkAvailability = async (sport, date) => {
    if (!sport || !date) return;

    const filteredCourts = courts.filter(c => (c.sport_type || c.sport_name) === sport);
    if (filteredCourts.length === 0) return;

    try {
      setLoading(true);
      const availabilityMap = {};

      for (const court of filteredCourts) {
        const response = await fetch(`${API_URL}/bookings/availability/${court.id}/${date}`);

        if (response.ok) {
          const existingBookings = await response.json();

          timeSlots.forEach(slot => {
            const key = `${court.id}-${slot.id}`;
            const booking = existingBookings.find(b =>
              slot.startTime < b.end_time && slot.endTime > b.start_time
            );

            availabilityMap[key] = booking ? {
              available: false,
              customerName: booking.customer_name,
              bookingId: booking.id
            } : {
              available: true
            };
          });
        } else {
          timeSlots.forEach(slot => {
            availabilityMap[`${court.id}-${slot.id}`] = { available: true };
          });
        }
      }

      setAvailability(availabilityMap);
    } catch (error) {
      console.error('Error checking availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSportSelect = (sport) => {
    setSelectedSport(sport);
    setSelectedSlots([]);
    setShowBookingForm(false);
    setError('');
    setSuccess('');
    if (selectedDate) {
      checkAvailability(sport, selectedDate);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedSlots([]);
    setShowBookingForm(false);
    setError('');
    setSuccess('');
    if (selectedSport) {
      checkAvailability(selectedSport, date);
    }
  };

  const handleSlotClick = (court, slot, slotData) => {
    if (!slotData.available) return;

    const now = new Date();
    const slotDateTime = new Date(`${selectedDate}T${slot.startTime}`);

    if (slotDateTime < now) {
      alert('Cannot book past time slots');
      return;
    }

    const existingIndex = selectedSlots.findIndex(s =>
      s.court.id === court.id && s.slot.id === slot.id
    );

    if (existingIndex >= 0) {
      setSelectedSlots(selectedSlots.filter((_, i) => i !== existingIndex));
    } else {
      const price = calculateSlotPrice(court.id, slot.startTime);
      setSelectedSlots([...selectedSlots, { court, slot, price }]);
    }
  };

  const submitBooking = async (e) => {
    e.preventDefault();

    if (selectedSlots.length === 0 || !customerData.name || !customerData.phone || !customerData.billNumber) {
      setError('Please select slots and fill in all required fields (Name, Phone, Bill Number)');
      return;
    }

    if (discount.percentage > 0 && !discount.reason.trim()) {
      setError('Discount reason is required when applying a discount');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const bookingPromises = selectedSlots.map(({ court, slot, price }, index) => {
        // Calculate discounted price per slot
        const slotDiscount = Math.round(price * discount.percentage / 100);
        const finalPrice = price - slotDiscount;

        // Build notes with discount info if applicable
        let notesWithDiscount = customerData.adminNotes || '';
        if (discount.percentage > 0) {
          const discountNote = `[Discount: ${discount.percentage}% - Reason: ${discount.reason}] Original: ₹${price}, Discount: ₹${slotDiscount}`;
          notesWithDiscount = notesWithDiscount ? `${notesWithDiscount}\n${discountNote}` : discountNote;
        }

        // Get staff name for discount tracking
        const staffName = user?.first_name
          ? `${user.first_name}${user.last_name ? ' ' + user.last_name : ''}`.trim()
          : user?.email?.split('@')[0] || 'Staff';

        const bookingPayload = {
          court_id: court.id,
          booking_date: selectedDate,
          start_time: slot.startTime,
          end_time: slot.endTime,
          customer_name: customerData.name,
          customer_phone: customerData.phone,
          customer_email: customerData.email || null,
          bill_number: selectedSlots.length > 1 ? `${customerData.billNumber}-${index + 1}` : customerData.billNumber,
          notes: notesWithDiscount || null,
          booking_source: 'frontdesk',
          paid_amount: finalPrice,
          amount_due: finalPrice,
          payment_status: 'paid',
          payment_mode: customerData.paymentMode,
          discount_percentage: discount.percentage,
          discount_reason: discount.reason || null,
          discount_applied_by: discount.percentage > 0 ? staffName : null,
          original_amount: price
        };

        return fetch(`${API_URL}/bookings`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(bookingPayload)
        }).then(res => res.json());
      });

      const results = await Promise.all(bookingPromises);

      if (results.every(r => r.id)) {
        setSuccess(`Successfully created ${results.length} booking${results.length > 1 ? 's' : ''}!`);
        setShowBookingForm(false);
        setCustomerData({ name: '', phone: '', email: '', billNumber: '', adminNotes: '', paymentMode: 'cash' });
        setDiscount({ percentage: 0, reason: '' });
        setSelectedSlots([]);
        checkAvailability(selectedSport, selectedDate);
      } else {
        setError('Some bookings failed. Please try again.');
      }
    } catch (error) {
      console.error('Error creating bookings:', error);
      setError('Failed to create bookings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourts();
    fetchPricingRules();
  }, []);

  const getMinDate = () => new Date().toISOString().split('T')[0];
  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 3);
    return maxDate.toISOString().split('T')[0];
  };

  const filteredCourts = courts.filter(c => (c.sport_type || c.sport_name) === selectedSport);
  const subtotal = selectedSlots.reduce((sum, s) => sum + s.price, 0);
  const discountAmount = Math.round(subtotal * discount.percentage / 100);
  const totalAmount = subtotal - discountAmount;

  if (loading && !courts.length) {
    return (
      <div className="quick-loading">
        <div className="quick-spinner"></div>
        <p>Loading courts...</p>
      </div>
    );
  }

  return (
    <div className="quick-booking">
      {/* Alerts */}
      {success && (
        <div className="quick-alert success">
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          {success}
        </div>
      )}

      {error && (
        <div className="quick-alert error">
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          {error}
        </div>
      )}

      {/* Selection Panel */}
      <div className="quick-panel">
        <div className="quick-filters">
          <div className="filter-group">
            <label>Sport</label>
            <select
              value={selectedSport}
              onChange={(e) => handleSportSelect(e.target.value)}
            >
              <option value="">Select a sport</option>
              {sports.map(sport => (
                <option key={sport} value={sport}>{sport}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Date</label>
            <input
              type="date"
              value={selectedDate}
              min={getMinDate()}
              max={getMaxDate()}
              onChange={(e) => handleDateSelect(e.target.value)}
            />
          </div>

          {selectedSlots.length > 0 && (
            <div className="filter-group selection-info">
              <span className="slot-count">{selectedSlots.length} slot{selectedSlots.length > 1 ? 's' : ''} = <strong>₹{totalAmount}</strong></span>
              <button onClick={() => setShowBookingForm(true)} className="proceed-btn">
                Book Now
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Availability Grid */}
      {selectedSport && selectedDate && filteredCourts.length > 0 && (
        <div className="quick-panel">
          <div className="grid-header">
            <h3>Select Time Slots</h3>
            <div className="grid-legend">
              <span className="legend-item available">Available</span>
              <span className="legend-item selected">Selected</span>
              <span className="legend-item booked">Booked</span>
            </div>
          </div>

          {loading ? (
            <div className="grid-loading">
              <div className="quick-spinner small"></div>
              <span>Checking availability...</span>
            </div>
          ) : (
            <div className="availability-grid">
              <table>
                <thead>
                  <tr>
                    <th className="time-col">Time</th>
                    {filteredCourts.map(court => (
                      <th key={court.id}>{court.court_name || court.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {timeSlots.map(slot => {
                    const now = new Date();
                    const slotDateTime = new Date(`${selectedDate}T${slot.startTime}`);
                    const isPast = slotDateTime < now;

                    return (
                      <tr key={slot.id}>
                        <td className="time-col">{slot.displayTime}</td>
                        {filteredCourts.map(court => {
                          const key = `${court.id}-${slot.id}`;
                          const slotData = availability[key] || { available: true };
                          const isSelected = selectedSlots.some(s =>
                            s.court.id === court.id && s.slot.id === slot.id
                          );
                          const price = calculateSlotPrice(court.id, slot.startTime);

                          let cellClass = 'slot-cell';
                          if (isPast) cellClass += ' past';
                          else if (isSelected) cellClass += ' selected';
                          else if (slotData.available) cellClass += ' available';
                          else cellClass += ' booked';

                          return (
                            <td key={key}>
                              <button
                                onClick={() => handleSlotClick(court, slot, slotData)}
                                disabled={!slotData.available || isPast}
                                className={cellClass}
                              >
                                {isPast ? '—' : isSelected ? `✓ ₹${price}` : slotData.available ? `₹${price}` : 'Booked'}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Booking Form Modal */}
      {showBookingForm && selectedSlots.length > 0 && (
        <div className="booking-modal-overlay" onClick={() => setShowBookingForm(false)}>
          <div className="booking-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Complete Booking</h2>
              <button className="close-btn" onClick={() => setShowBookingForm(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              {/* Selected Slots Summary */}
              <div className="slots-summary">
                <h4>Selected Slots ({selectedSlots.length})</h4>
                <div className="slots-list">
                  {selectedSlots.map(({ court, slot, price }, idx) => (
                    <div key={idx} className="slot-item">
                      <span className="slot-court">{court.court_name || court.name}</span>
                      <span className="slot-time">{slot.displayTime}</span>
                      <span className="slot-price">₹{price}</span>
                    </div>
                  ))}
                </div>

                {/* Discount Section */}
                <div className="discount-section">
                  <div className="discount-row">
                    <label>Discount %</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={discount.percentage}
                      onChange={(e) => setDiscount({ ...discount, percentage: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) })}
                      className="discount-input"
                      placeholder="0"
                    />
                  </div>
                  {discount.percentage > 0 && (
                    <div className="discount-reason-row">
                      <label>Discount Reason *</label>
                      <input
                        type="text"
                        value={discount.reason}
                        onChange={(e) => setDiscount({ ...discount, reason: e.target.value })}
                        placeholder="Enter reason for discount (required)"
                        className="discount-reason-input"
                        required
                      />
                    </div>
                  )}
                </div>

                <div className="slots-total-section">
                  <div className="subtotal-row">
                    <span>Subtotal</span>
                    <span>₹{subtotal}</span>
                  </div>
                  {discount.percentage > 0 && (
                    <div className="discount-row-summary">
                      <span>Discount ({discount.percentage}%)</span>
                      <span className="discount-value">-₹{discountAmount}</span>
                    </div>
                  )}
                  <div className="slots-total">
                    <span>Total</span>
                    <span className="total-value">₹{totalAmount}</span>
                  </div>
                </div>
              </div>

              {/* Customer Form */}
              <form onSubmit={submitBooking} className="customer-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Customer Name *</label>
                    <input
                      type="text"
                      value={customerData.name}
                      onChange={(e) => setCustomerData({ ...customerData, name: e.target.value })}
                      required
                      placeholder="Enter name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone Number *</label>
                    <input
                      type="tel"
                      value={customerData.phone}
                      onChange={(e) => setCustomerData({ ...customerData, phone: e.target.value })}
                      required
                      placeholder="Enter phone"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Bill Number *</label>
                    <input
                      type="text"
                      value={customerData.billNumber}
                      onChange={(e) => setCustomerData({ ...customerData, billNumber: e.target.value.toUpperCase() })}
                      required
                      placeholder="Enter bill/receipt number"
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Payment Mode *</label>
                    <select
                      value={customerData.paymentMode}
                      onChange={(e) => setCustomerData({ ...customerData, paymentMode: e.target.value })}
                      required
                      style={{ padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px' }}
                    >
                      <option value="cash">Cash</option>
                      <option value="upi">UPI</option>
                      <option value="card">Card</option>
                      <option value="online">Online</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Email (Optional)</label>
                    <input
                      type="email"
                      value={customerData.email}
                      onChange={(e) => setCustomerData({ ...customerData, email: e.target.value })}
                      placeholder="Enter email"
                    />
                  </div>
                  <div className="form-group">
                    {/* Empty for alignment */}
                  </div>
                </div>

                <div className="form-group">
                  <label>Admin Notes</label>
                  <textarea
                    value={customerData.adminNotes}
                    onChange={(e) => setCustomerData({ ...customerData, adminNotes: e.target.value })}
                    rows={2}
                    placeholder="Internal notes (optional)"
                  />
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-cancel" onClick={() => setShowBookingForm(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-confirm" disabled={loading}>
                    {loading ? 'Creating...' : `Confirm Booking (₹${totalAmount})`}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .quick-booking {
          padding: 0;
        }

        .quick-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 300px;
          color: #6b7280;
        }

        .quick-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #e5e7eb;
          border-top-color: ${primaryColor};
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin-bottom: 12px;
        }

        .quick-spinner.small {
          width: 24px;
          height: 24px;
          border-width: 2px;
          margin-bottom: 0;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Alerts */
        .quick-alert {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 18px;
          border-radius: 10px;
          margin-bottom: 20px;
          font-weight: 500;
        }

        .quick-alert svg {
          width: 22px;
          height: 22px;
          flex-shrink: 0;
        }

        .quick-alert.success {
          background: #ecfdf5;
          color: #065f46;
          border: 1px solid #a7f3d0;
        }

        .quick-alert.error {
          background: #fef2f2;
          color: #991b1b;
          border: 1px solid #fecaca;
        }

        /* Panel */
        .quick-panel {
          background: white;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          padding: 20px;
          margin-bottom: 20px;
        }

        /* Filters - Inline Row */
        .quick-filters {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          align-items: flex-end;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .filter-group label {
          font-size: 11px;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.025em;
        }

        .filter-group select,
        .filter-group input[type="date"] {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          font-family: inherit;
          min-width: 160px;
          height: 38px;
          transition: all 0.15s;
        }

        .filter-group select:focus,
        .filter-group input:focus {
          outline: none;
          border-color: ${primaryColor};
          box-shadow: 0 0 0 2px ${primaryColor}26;
        }

        /* Selection Info - Inline with filters */
        .selection-info {
          flex-direction: row !important;
          align-items: center !important;
          gap: 12px !important;
          margin-left: auto;
          padding: 6px 12px;
          background: #ecfdf5;
          border-radius: 6px;
          border: 1px solid #a7f3d0;
        }

        .slot-count {
          font-size: 13px;
          color: #065f46;
        }

        .proceed-btn {
          padding: 8px 16px;
          background: ${primaryColor};
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s;
        }

        .proceed-btn:hover {
          filter: brightness(0.9);
        }

        /* Grid */
        .grid-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
        }

        .grid-header h3 {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          color: #1f2937;
        }

        .grid-legend {
          display: flex;
          gap: 16px;
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #6b7280;
        }

        .legend-item::before {
          content: '';
          width: 14px;
          height: 14px;
          border-radius: 4px;
        }

        .legend-item.available::before {
          background: #10b981;
        }

        .legend-item.selected::before {
          background: ${primaryColor};
        }

        .legend-item.booked::before {
          background: #e5e7eb;
        }

        .grid-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 40px;
          color: #6b7280;
        }

        .availability-grid {
          overflow: auto;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          max-height: 400px;
        }

        .availability-grid table {
          width: 100%;
          border-collapse: collapse;
          min-width: 500px;
        }

        .availability-grid th,
        .availability-grid td {
          padding: 0;
          border-bottom: 1px solid #f3f4f6;
        }

        .availability-grid th {
          padding: 8px 4px;
          background: #f9fafb;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.025em;
          color: #6b7280;
          text-align: center;
        }

        .availability-grid th.time-col,
        .availability-grid td.time-col {
          text-align: left;
          padding-left: 12px;
          width: 110px;
          white-space: nowrap;
          background: #fafbfc;
          font-weight: 600;
          font-size: 12px;
          color: #374151;
        }

        .availability-grid tbody tr:last-child td {
          border-bottom: none;
        }

        /* Slot Cells - Compact */
        .slot-cell {
          display: flex;
          align-items: center;
          justify-content: center;
          width: calc(100% - 4px);
          padding: 4px 2px;
          border: none;
          background: transparent;
          cursor: pointer;
          transition: all 0.15s;
          min-height: 28px;
          border-radius: 4px;
          margin: 2px;
          font-size: 11px;
        }

        .slot-cell.available {
          background: #d1fae5;
          color: #065f46;
        }

        .slot-cell.available:hover {
          background: #a7f3d0;
        }

        .slot-cell.selected {
          background: ${primaryColor};
          color: white;
        }

        .slot-cell.booked {
          background: #f3f4f6;
          color: #9ca3af;
          cursor: not-allowed;
        }

        .slot-cell.past {
          background: #f9fafb;
          color: #d1d5db;
          cursor: not-allowed;
        }

        /* Modal */
        .booking-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .booking-modal {
          background: white;
          border-radius: 16px;
          width: 100%;
          max-width: 500px;
          max-height: 90vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 700;
          color: #1f2937;
        }

        .close-btn {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          background: #f3f4f6;
          border-radius: 8px;
          cursor: pointer;
          color: #6b7280;
          transition: all 0.2s;
        }

        .close-btn:hover {
          background: #e5e7eb;
          color: #374151;
        }

        .close-btn svg {
          width: 20px;
          height: 20px;
        }

        .modal-body {
          padding: 24px;
          overflow-y: auto;
        }

        /* Slots Summary */
        .slots-summary {
          background: #f9fafb;
          border-radius: 10px;
          padding: 16px;
          margin-bottom: 24px;
        }

        .slots-summary h4 {
          margin: 0 0 12px;
          font-size: 13px;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.025em;
        }

        .slots-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          max-height: 150px;
          overflow-y: auto;
        }

        .slot-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 12px;
          background: white;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
        }

        .slot-court {
          font-weight: 600;
          color: #1f2937;
          font-size: 14px;
        }

        .slot-time {
          color: #6b7280;
          font-size: 13px;
        }

        .slot-item .slot-price {
          color: ${primaryColor};
          font-weight: 600;
        }

        /* Discount Section */
        .discount-section {
          margin-top: 12px;
          padding: 12px;
          background: #fefce8;
          border: 1px solid #fef08a;
          border-radius: 8px;
        }

        .discount-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        .discount-row label {
          font-size: 13px;
          font-weight: 600;
          color: #854d0e;
        }

        .discount-input {
          width: 80px;
          padding: 6px 10px;
          border: 1px solid #fde047;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
          text-align: center;
          background: white;
        }

        .discount-input:focus {
          outline: none;
          border-color: #eab308;
          box-shadow: 0 0 0 2px rgba(234, 179, 8, 0.2);
        }

        .discount-reason-row {
          margin-top: 10px;
        }

        .discount-reason-row label {
          display: block;
          font-size: 12px;
          font-weight: 600;
          color: #854d0e;
          margin-bottom: 4px;
        }

        .discount-reason-input {
          width: 100%;
          padding: 8px 10px;
          border: 1px solid #fde047;
          border-radius: 6px;
          font-size: 13px;
          background: white;
        }

        .discount-reason-input:focus {
          outline: none;
          border-color: #eab308;
          box-shadow: 0 0 0 2px rgba(234, 179, 8, 0.2);
        }

        .slots-total-section {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 2px solid #e5e7eb;
        }

        .subtotal-row {
          display: flex;
          justify-content: space-between;
          font-size: 13px;
          color: #6b7280;
          margin-bottom: 4px;
        }

        .discount-row-summary {
          display: flex;
          justify-content: space-between;
          font-size: 13px;
          color: #16a34a;
          margin-bottom: 4px;
        }

        .discount-value {
          font-weight: 600;
        }

        .slots-total {
          display: flex;
          justify-content: space-between;
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px dashed #e5e7eb;
          font-weight: 600;
          color: #1f2937;
        }

        .total-value {
          font-size: 1.25rem;
          font-weight: 700;
          color: ${primaryColor};
        }

        /* Form */
        .customer-form .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .customer-form .form-group {
          margin-bottom: 16px;
        }

        .customer-form label {
          display: block;
          margin-bottom: 6px;
          font-size: 14px;
          font-weight: 600;
          color: #374151;
        }

        .customer-form input,
        .customer-form textarea {
          width: 100%;
          padding: 10px 14px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 15px;
          font-family: inherit;
          transition: all 0.2s;
        }

        .customer-form input:focus,
        .customer-form textarea:focus {
          outline: none;
          border-color: ${primaryColor};
          box-shadow: 0 0 0 3px ${primaryColor}26;
        }

        .customer-form textarea {
          resize: none;
        }

        .form-actions {
          display: flex;
          gap: 12px;
          margin-top: 8px;
        }

        .btn-cancel {
          flex: 1;
          padding: 12px;
          background: #f3f4f6;
          color: #374151;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-cancel:hover {
          background: #e5e7eb;
        }

        .btn-confirm {
          flex: 2;
          padding: 12px;
          background: ${primaryColor};
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-confirm:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        .btn-confirm:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        /* Responsive */
        @media (max-width: 640px) {
          .quick-filters {
            grid-template-columns: 1fr;
          }

          .customer-form .form-row {
            grid-template-columns: 1fr;
          }

          .grid-legend {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default AdminQuickBooking;
